import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  IBulkImportStatus,
  IBulkImportSummary,
  ICustomFieldDefinition,
  ICustomFieldTemplate,
  IHospitalDetail,
  IMedicationSourceImpact,
  IMedicationSourceOption,
  IWardCustomField,
} from "../../../@types/admin/AdminHospitalDetails";
import { IAdminPatientListItem } from "../../../@types/admin/AdminPatient";
import { IStaffListItem } from "../../../@types/admin/AdminStaff";
import { IWard } from "../../../@types/admin/AdminWard";
import { IPaginatedResponse } from "../../../@types/PaginatedResponse";
import { IWardStandardField } from "../../../@types/StandardField";
import { hospitalDetailsService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

/**
 * Stable empty arrays for the `data ?? []` defaults below.
 *
 * ⚠️ A literal `[]` there is a NEW array identity on every render, which is an
 * infinite loop for any consumer with `useEffect(..., [theList])`:
 * effect → setState → render → fresh `[]` → effect. `WardCustomFieldsModal`
 * seeds its form from `useWardCustomFields` that way and hit exactly this
 * ("Maximum update depth exceeded"). One frozen constant per list type keeps the
 * identity stable while the query is idle or loading.
 */
const EMPTY_WARDS: IWard[] = [];
const EMPTY_STAFF: IStaffListItem[] = [];
const EMPTY_CUSTOM_FIELDS: ICustomFieldDefinition[] = [];
const EMPTY_TEMPLATES: ICustomFieldTemplate[] = [];
const EMPTY_WARD_CUSTOM_FIELDS: IWardCustomField[] = [];
const EMPTY_WARD_STANDARD_FIELDS: IWardStandardField[] = [];
const EMPTY_BULK_IMPORTS: IBulkImportSummary[] = [];
const EMPTY_PATIENTS: IAdminPatientListItem[] = [];
const EMPTY_MEDICATION_SOURCES: IMedicationSourceOption[] = [];

/** Query keys the mutations invalidate. */
export const HOSPITAL_DETAIL_QUERY_KEY = "admin-hospital-detail";
export const HOSPITAL_WARDS_QUERY_KEY = "admin-hospital-wards";
export const HOSPITAL_PATIENTS_QUERY_KEY = "admin-hospital-patients";
export const HOSPITAL_STAFF_QUERY_KEY = "admin-hospital-staff";
export const CUSTOM_FIELDS_QUERY_KEY = "admin-hospital-custom-fields";
export const WARD_CUSTOM_FIELDS_QUERY_KEY = "admin-ward-custom-fields";
export const WARD_STANDARD_FIELDS_QUERY_KEY = "admin-ward-standard-fields";
export const BULK_IMPORTS_QUERY_KEY = "admin-bulk-imports";

/** One hospital's record. Idle until an id is known. */
export function useHospitalDetail(hospitalId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IHospitalDetail>({
    queryKey: [HOSPITAL_DETAIL_QUERY_KEY, hospitalId],
    queryFn: async () =>
      ensureOk(await hospitalDetailsService.getHospital(hospitalId!)),
    enabled: hospitalId !== undefined,
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read.
    refetchOnMount: "always",
  });

  return { hospital: data, isLoading, isError };
}

/** The medication catalogues a hospital can pick, with their loaded release. */
export function useMedicationSources() {
  const { data, isLoading, isError } = useQuery<IMedicationSourceOption[]>({
    queryKey: ["admin-medication-sources"],
    queryFn: async () =>
      ensureOk(await hospitalDetailsService.getMedicationSources()),
  });

  return {
    medicationSources: data ?? EMPTY_MEDICATION_SOURCES,
    isLoading,
    isError,
  };
}

/**
 * Patients a source change would affect, for the warning. Only fetched once
 * the admin has picked a different source.
 */
export function useMedicationSourceImpact(
  hospitalId: number | undefined,
  enabled: boolean
) {
  const { data, isLoading } = useQuery<IMedicationSourceImpact>({
    queryKey: ["admin-medication-source-impact", hospitalId],
    queryFn: async () =>
      ensureOk(
        await hospitalDetailsService.getMedicationSourceImpact(hospitalId!)
      ),
    enabled: enabled && hospitalId !== undefined,
    refetchOnMount: "always",
  });

  return { impact: data, isLoading };
}

/** The hospital's wards. */
export function useHospitalWards(hospitalId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IWard[]>({
    queryKey: [HOSPITAL_WARDS_QUERY_KEY, hospitalId],
    queryFn: async () => {
      const response = ensureOk(
        await hospitalDetailsService.getHospitalWards(hospitalId!)
      );
      return response.result ?? [];
    },
    enabled: hospitalId !== undefined,
    refetchOnMount: "always",
  });

  return { wards: data ?? EMPTY_WARDS, isLoading, isError };
}

/** The hospital's patients, server-paginated with server-side search. */
export function useHospitalPatients(
  hospitalId: number | undefined,
  page: number,
  pageSize: number,
  search?: string
) {
  const { data, isLoading, isFetching, isError } = useQuery<
    IPaginatedResponse<IAdminPatientListItem[]>
  >({
    queryKey: [
      HOSPITAL_PATIENTS_QUERY_KEY,
      hospitalId,
      page,
      pageSize,
      search ?? "",
    ],
    queryFn: async () =>
      ensureOk(
        await hospitalDetailsService.getHospitalPatients(
          hospitalId!,
          page,
          pageSize,
          search
        )
      ),
    enabled: hospitalId !== undefined,
    placeholderData: keepPreviousData,
    refetchOnMount: "always",
  });

  return {
    patients: data?.result ?? EMPTY_PATIENTS,
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isFetching,
    isError,
  };
}

/** The hospital's staff. */
export function useHospitalStaff(hospitalId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IStaffListItem[]>({
    queryKey: [HOSPITAL_STAFF_QUERY_KEY, hospitalId],
    queryFn: async () => {
      const response = ensureOk(
        await hospitalDetailsService.getHospitalStaff(hospitalId!)
      );
      return response.result ?? [];
    },
    enabled: hospitalId !== undefined,
    refetchOnMount: "always",
  });

  return { staff: data ?? EMPTY_STAFF, isLoading, isError };
}

/**
 * The hospital's custom-field definitions, inactive ones included — the page
 * has to offer reactivation, so it cannot filter them out.
 */
export function useCustomFields(hospitalGroupId: number | undefined) {
  const { data, isLoading, isError } = useQuery<ICustomFieldDefinition[]>({
    queryKey: [CUSTOM_FIELDS_QUERY_KEY, hospitalGroupId],
    queryFn: async () =>
      ensureOk(await hospitalDetailsService.getCustomFields(hospitalGroupId!)),
    enabled: hospitalGroupId !== undefined,
    refetchOnMount: "always",
  });

  return { customFields: data ?? EMPTY_CUSTOM_FIELDS, isLoading, isError };
}

/** Presets, flagged with whether this hospital already has each. */
export function useCustomFieldTemplates(hospitalGroupId: number | undefined) {
  const { data, isLoading, isError } = useQuery<ICustomFieldTemplate[]>({
    queryKey: ["admin-custom-field-templates", hospitalGroupId],
    queryFn: async () =>
      ensureOk(
        await hospitalDetailsService.getCustomFieldTemplates(hospitalGroupId!)
      ),
    enabled: hospitalGroupId !== undefined,
  });

  return { templates: data ?? EMPTY_TEMPLATES, isLoading, isError };
}

/** How one ward uses the hospital's definitions. Idle until a ward is chosen. */
export function useWardCustomFields(wardId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IWardCustomField[]>({
    queryKey: [WARD_CUSTOM_FIELDS_QUERY_KEY, wardId],
    queryFn: async () =>
      ensureOk(await hospitalDetailsService.getWardCustomFields(wardId!)),
    enabled: wardId !== undefined,
    refetchOnMount: "always",
  });

  return {
    wardCustomFields: data ?? EMPTY_WARD_CUSTOM_FIELDS,
    isLoading,
    isError,
  };
}

/**
 * EN-5192: the ward's settings for the built-in onboarding fields. Idle until a ward is chosen.
 */
export function useWardStandardFields(wardId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IWardStandardField[]>({
    queryKey: [WARD_STANDARD_FIELDS_QUERY_KEY, wardId],
    queryFn: async () =>
      ensureOk(await hospitalDetailsService.getWardStandardFields(wardId!)),
    enabled: wardId !== undefined,
    refetchOnMount: "always",
  });

  return {
    wardStandardFields: data ?? EMPTY_WARD_STANDARD_FIELDS,
    isLoading,
    isError,
  };
}

/** Bulk import jobs, newest first. */
export function useBulkImports(enabled = true) {
  const { data, isLoading, isError } = useQuery<IBulkImportSummary[]>({
    queryKey: [BULK_IMPORTS_QUERY_KEY],
    queryFn: async () => {
      const response = ensureOk(await hospitalDetailsService.getBulkImports());
      return response.result ?? [];
    },
    enabled,
    refetchOnMount: "always",
  });

  return { bulkImports: data ?? EMPTY_BULK_IMPORTS, isLoading, isError };
}

/**
 * One job with its per-row results.
 *
 * Polls while the job is unfinished: the import runs in the background, so a
 * job opened straight after upload has no results yet. Retool made the operator
 * press Refresh.
 */
export function useBulkImportStatus(jobId: string | undefined) {
  const { data, isLoading, isError } = useQuery<IBulkImportStatus>({
    queryKey: ["admin-bulk-import-status", jobId],
    queryFn: async () =>
      ensureOk(await hospitalDetailsService.getBulkImportStatus(jobId!)),
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const status = query.state.data?.status?.toLowerCase();
      // Anything but a terminal state keeps polling.
      return status === "completed" || status === "failed" ? false : 2000;
    },
    refetchOnMount: "always",
  });

  return { job: data, isLoading, isError };
}
